import { useState, useEffect, useCallback } from "react";
import RpisPage from "../RpisPage/RpisPage";
import Messages from "../../localization/Messages";
import { useIntl } from "react-intl";
import "./LDManageAddresses.css";
import SidebarLayout from "../SidebarLayout/SidebarLayout";
import {
    Autocomplete,
    Button,
    FilledInput,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
} from "@mui/material";
import { TableSkeleton } from "../../components/TableSkeleton/TableSkeleton";
import AddressForm from "./AddressForm";
import { AddressType, AddressTypes, IAddress } from "../../models/address";
import { deactivateAddress, deleteAddress, searchAddress } from "../../api/address";
import { searchParamsInit } from "../../models/searchParams";
import { useDispatch } from "react-redux";
import { showBackendMessage } from "../../helpers/messagesHelper";
import EditIcon from "@mui/icons-material/Edit";
import classNames from "classnames";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { SortOrder, useTableSort } from "../../hooks/useTableSort";
import { DatatablePaginationFilters, useGuiConfState } from "../../hooks/useGuiConfState";
import TableCellSorted from "../../components/TableCellSorted";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { showToastMessage } from "../../actions/toastMessageActions";
import MoreVertIcon from "@mui/icons-material/MoreVert";

export type LDManageAddressesFilters = DatatablePaginationFilters & {
    addressType?: AddressType;
    status: "all" | "active" | "inactive";
    textSearch: string;
    order: SortOrder;
};

const addressStatusFilters = [
    { id: "all", label: "All", value: "all" as const },
    { id: "active", label: "active", value: "active" as const },
    { id: "inactive", label: "inactive", value: "inactive" as const },
];

function buildFilters(filters: LDManageAddressesFilters) {
    const filterArray: object[] = [];
    if (filters.addressType) {
        filterArray.push({ type: filters.addressType });
    }
    if (filters.status === "active") {
        filterArray.push({ inactive: 0 });
    }
    if (filters.status === "inactive") {
        filterArray.push({ inactive: 1 });
    }
    return { $and: filterArray };
}

function buildPayload(filters: LDManageAddressesFilters) {
    return {
        ...searchParamsInit,
        offset: filters.page * filters.rowsPerPage,
        limit: filters.rowsPerPage,
        order: filters.order,
        textSearch: filters.textSearch,
        filter: buildFilters(filters),
    };
}

const LD_ADDRESSES_SORTABLE_COLUMNS = [
    "type",
    "company",
    "street",
    "firstName",
    "tel",
    "email",
] as const;

export const errorMessages = [
    {
        id: 0,
        messageCode: "invoiceAddressInUseVoyages",
        message: Messages.invoiceAddressInUseVoyages,
    },
    {
        id: 1,
        messageCode: "invoiceAddressInUseActiveBooking",
        message: Messages.invoiceAddressInUseActiveBooking,
    },
];

/**
 * Represents the Locks & Docks Manage addresses page.
 * This page allows users to search, add, edit, and delete addresses.
 * It displays a table of addresses with sortable columns and provides filtering options.
 * It features a sidebar with an address form for adding or editing addresses.
 *
 * @returns The `ManageAddresses` component.
 */
export const ManageAddresses = () => {
    const withConfirm = useConfirmDialog();
    const dispatch = useDispatch();
    const intl = useIntl();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [addresses, setAddresses] = useState<IAddress[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<IAddress>();
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [menuAddress, setMenuAddress] = useState<IAddress | undefined>();
    const [totalAddresses, setTotalAddresses] = useState(0);
    const [filters, setFilters] = useGuiConfState("ldManageAddresses");
    const registerSort = useTableSort<typeof LD_ADDRESSES_SORTABLE_COLUMNS>({
        columns: LD_ADDRESSES_SORTABLE_COLUMNS,
        value: filters.order,
        onChange: order => setFilters({ order }),
    });

    const getAddresses = useCallback(async () => {
        try {
            setLoading(true);
            const res = await searchAddress(buildPayload(filters));
            setAddresses(res.data.rows ?? []);
            setTotalAddresses(res.data.total ?? 0);
        } catch {
            dispatch(showBackendMessage(intl, "error", "fetching", Messages.addresses));
        } finally {
            setLoading(false);
        }
    }, [intl, filters, dispatch]);

    useEffect(() => {
        getAddresses();
    }, [getAddresses]);

    const onAddAddress = () => {
        setSidebarOpen(false);
        getAddresses();
    };

    const handleSelectAddress = (row: IAddress) => {
        setSelectedAddress(row);
        setSidebarOpen(true);
    };

    const handleDeleteAddress = async (id: string) => {
        try {
            setLoading(true);
            await deleteAddress(id);
            dispatch(showBackendMessage(intl, "success", "deleting", Messages.address));
        } catch (err: any) {
            const msg = err.response.data.message as string;
            if (msg) {
                const message = errorMessages.find(message => message.messageCode === msg)?.message;
                dispatch(
                    showToastMessage(
                        intl.formatMessage(message ?? Messages.errorDeletingAddress),
                        "error",
                        10000,
                    ),
                );
            } else {
                dispatch(showBackendMessage(intl, "error", "deleting", Messages.address));
            }
        } finally {
            setSelectedAddress(undefined);
            getAddresses();
        }
    };

    const handleDeactivateAddress = async (id: string) => {
        try {
            setLoading(true);
            await deactivateAddress(id);
            dispatch(
                showToastMessage(
                    intl.formatMessage(Messages.invoiceAddressDeactivatedSuccess),
                    "success",
                    5000,
                ),
            );
        } catch {
            dispatch(
                showToastMessage(
                    intl.formatMessage(Messages.invoiceAddressDeactivatedError),
                    "error",
                    8000,
                ),
            );
        } finally {
            setSelectedAddress(undefined);
            getAddresses();
        }
    };

    const openRowMenu = (event: React.MouseEvent<HTMLElement>, row: IAddress) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuAddress(row);
    };

    const closeRowMenu = () => {
        setMenuAnchorEl(null);
        setMenuAddress(undefined);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setFilters({ page: newPage });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilters({
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0,
        });
    };

    const clearFilters = () => {
        setFilters({
            addressType: undefined,
            status: "all",
            textSearch: "",
            page: 0,
        });
    };

    return (
        <RpisPage
            title={intl.formatMessage(Messages.manageAddresses)}
            className="ld-manage-addresses-container"
        >
            <SidebarLayout
                sidebarTitle={intl.formatMessage(Messages.manageAddresses)}
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                sidebarContent={
                    <AddressForm
                        selectedAddress={selectedAddress}
                        callback={onAddAddress}
                        setSidebarOpen={setSidebarOpen}
                    />
                }
            >
                <div className="title-and-actions-container">
                    <form id="search-form">
                        <Autocomplete
                            id="address-type-autocomplete"
                            options={AddressTypes}
                            getOptionLabel={o => intl.formatMessage(o.label)}
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    label={intl.formatMessage(Messages.addressType)}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {intl.formatMessage(option.label)}
                                </li>
                            )}
                            onChange={(_, value) => setFilters({ addressType: value?.type })}
                            value={
                                AddressTypes.find(add => add.type === filters.addressType) ?? null
                            }
                        />
                        <FormControl variant="filled">
                            <FilledInput
                                onChange={e => setFilters({ textSearch: e.target.value })}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                                value={filters.textSearch}
                                autoFocus={true}
                            />
                            <InputLabel>{intl.formatMessage(Messages.search)}</InputLabel>
                        </FormControl>
                        <Autocomplete
                            id="address-status"
                            options={addressStatusFilters}
                            getOptionLabel={o => {
                                if (o.label === "active")
                                    return intl.formatMessage(Messages.active);
                                if (o.label === "inactive")
                                    return intl.formatMessage(Messages.inactive);
                                return intl.formatMessage(Messages.all);
                            }}
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    label={intl.formatMessage(Messages.status)}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.label === "active"
                                        ? intl.formatMessage(Messages.active)
                                        : option.label === "inactive"
                                          ? intl.formatMessage(Messages.inactive)
                                          : intl.formatMessage(Messages.all)}
                                </li>
                            )}
                            onChange={(_, value) => setFilters({ status: value?.value ?? "all" })}
                            value={
                                addressStatusFilters.find(
                                    status => status.value === filters.status,
                                ) ?? null
                            }
                        />
                        {(filters.addressType ||
                            filters.textSearch ||
                            filters.status !== "all") && (
                            <Button variant="outlined" onClick={clearFilters}>
                                {intl.formatMessage(Messages.clear)}
                            </Button>
                        )}
                    </form>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setSidebarOpen(true);
                            setSelectedAddress(undefined);
                        }}
                    >
                        {intl.formatMessage(Messages.addAddress)}
                    </Button>
                </div>

                <TableContainer className="manage-addresses-table-container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCellSorted
                                    label={intl.formatMessage(Messages.addressType)}
                                    {...registerSort("type")}
                                />
                                <TableCellSorted
                                    align="right"
                                    label={intl.formatMessage(Messages.companyName)}
                                    {...registerSort("company")}
                                />
                                <TableCellSorted
                                    align="right"
                                    label={intl.formatMessage(Messages.address)}
                                    {...registerSort("street")}
                                />
                                <TableCellSorted
                                    align="right"
                                    label={intl.formatMessage(Messages.contactName)}
                                    {...registerSort("firstName")}
                                />
                                <TableCellSorted
                                    align="right"
                                    label={intl.formatMessage(Messages.telephone)}
                                    {...registerSort("tel")}
                                />
                                <TableCellSorted
                                    align="right"
                                    label={intl.formatMessage(Messages.email)}
                                    {...registerSort("email")}
                                />
                                <TableCell align="right">
                                    {intl.formatMessage(Messages.status)}
                                </TableCell>
                                <TableCell align="center">
                                    {intl.formatMessage(Messages.action)}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        {loading ? (
                            <TableSkeleton columns={8} rows={10} />
                        ) : (
                            <TableBody>
                                {addresses.map(row => (
                                    <TableRow
                                        key={row.id}
                                        className={classNames({
                                            "active-table-row":
                                                selectedAddress?.id === row.id && sidebarOpen,
                                        })}
                                    >
                                        <TableCell>
                                            {intl.formatMessage(
                                                AddressTypes.find(add => add.type === row.type)
                                                    ?.label ?? Messages.unavailable,
                                            )}
                                        </TableCell>
                                        <TableCell align="right">{row.company}</TableCell>
                                        <TableCell align="right">{`${row.street ?? ""} ${
                                            row.streetNo ?? ""
                                        }, ${row.zip ?? ""} ${row.city ?? ""}`}</TableCell>
                                        <TableCell align="right">{`${row.firstName ?? ""} ${
                                            row.surname ?? ""
                                        }`}</TableCell>
                                        <TableCell align="right">{row.tel ?? ""}</TableCell>
                                        <TableCell align="right">{row.email}</TableCell>
                                        <TableCell align="right">
                                            {row.inactive === 1
                                                ? intl.formatMessage(Messages.inactive)
                                                : intl.formatMessage(Messages.active)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                aria-label={intl.formatMessage(Messages.action)}
                                                onClick={event => openRowMenu(event, row)}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>
                <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl && menuAddress)}
                    onClose={closeRowMenu}
                >
                    {menuAddress && menuAddress?.type !== AddressType.InvoiceAddress && (
                        <MenuItem
                            onClick={() => {
                                handleSelectAddress(menuAddress);
                                closeRowMenu();
                            }}
                        >
                            <EditIcon fontSize="small" style={{ marginRight: 8 }} />
                            {intl.formatMessage(Messages.edit)}
                        </MenuItem>
                    )}
                    {menuAddress?.type === AddressType.InvoiceAddress &&
                        menuAddress?.inactive !== 1 && (
                            <MenuItem
                                onClick={() => {
                                    const selected = menuAddress;
                                    closeRowMenu();
                                    if (!selected) return;
                                    withConfirm({
                                        title: intl.formatMessage(
                                            Messages.deactivateInvoiceAddressTitle,
                                        ),
                                        message: intl.formatMessage(
                                            Messages.deactivateInvoiceAddressConfirm,
                                        ),
                                        onConfirm: async () =>
                                            await handleDeactivateAddress(selected.id),
                                    });
                                }}
                            >
                                {intl.formatMessage(Messages.deactivate)}
                            </MenuItem>
                        )}
                    <MenuItem
                        onClick={() => {
                            const selected = menuAddress;
                            closeRowMenu();
                            if (!selected) return;
                            withConfirm({
                                message: intl.formatMessage(Messages.confirmAddressDelete),
                                onConfirm: async () => await handleDeleteAddress(selected.id),
                            });
                        }}
                    >
                        <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
                        {intl.formatMessage(Messages.delete)}
                    </MenuItem>
                </Menu>
                <TablePagination
                    rowsPerPageOptions={[50, 100, 250, 500]}
                    component="div"
                    count={totalAddresses}
                    rowsPerPage={filters.rowsPerPage}
                    page={filters.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </SidebarLayout>
        </RpisPage>
    );
};

export default ManageAddresses;
